import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { ProductFormData } from "../../../shared/schemas/product.schema";
import { trpcClient } from "@/lib/trpc";
import ProductForm from "./ProductForm";
import { productToFormValues } from "./product-form-values";
import { toast } from "sonner";
import { useState } from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { sanitizeReturnUrl } from "@/lib/utils";
import { useProductTypeLookup, useMaterialTypeLookup, useProcessLookup } from "@/hooks/useFKLookup";

export default function ProductEdit() {
  const { productId } = useParams({ strict: false }) as { productId: string };
  const search = useSearch({ strict: false }) as { returnUrl?: string };
  const rawReturnUrl = search?.returnUrl;
  const returnUrl = rawReturnUrl ? sanitizeReturnUrl(rawReturnUrl, "") : undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { options: productTypeOptions } = useProductTypeLookup();
  const { options: materialTypeOptions } = useMaterialTypeLookup();
  const { options: processOptions } = useProcessLookup();

  const handleBack = () => {
    if (returnUrl) {
      navigate({ to: returnUrl as any });
    } else if (window.history.length > 2) {
      window.history.back();
    } else {
      navigate({ to: "/products/$productId", params: { productId } });
    }
  };

  const { data: productData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      return trpcClient.products.getById.query({ id: productId });
    },
  });

  const product = productData as any;

  const [bypassActivePoCheck, setBypassActivePoCheck] = useState(false);

  const handleSubmit = async (data: ProductFormData) => {
    try {
      const payload: any = {
        id: productId,
        data: { ...data as any, ...(bypassActivePoCheck ? { _bypassActivePoCheck: true } : {}) },
      };
      await trpcClient.products.update.mutate(payload);
      setBypassActivePoCheck(false);

      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["product", productId] });
      await queryClient.invalidateQueries({ queryKey: ["order"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });

      toast.success("Cập nhật sản phẩm thành công");
      if (returnUrl) {
        navigate({ to: returnUrl as any, replace: true });
      } else if (window.history.length > 2) {
        window.history.back();
      } else {
        navigate({ to: "/products/$productId", params: { productId }, replace: true });
      }
      return true;
    } catch (err: any) {
      const msg = err?.message || "Cập nhật sản phẩm thất bại";
      // Nếu lỗi cảnh báo BOM/routing có Lệnh SX, cho phép user bấm lưu lần nữa để bypass
      if (msg.includes('Lệnh SX hoạt động') && !bypassActivePoCheck) {
        toast.warning(msg + " Bấm Cập nhật lần nữa để xác nhận.");
        setBypassActivePoCheck(true);
        return false;
      }
      toast.error(msg);
      throw err;
    }
  };


  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]} />;
  }

  if (detailError || !product) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]}
        entityLabel="Sản phẩm"
        backTo="/products"
        codeOrId={productId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }

  return (
    <StandardDetailLayout
      title={`Cập nhật Sản Phẩm`}
      breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]}
      showContextPanel={false}
      onBack={handleBack}
      overrideBack={!!returnUrl}
    >
      <div className="w-full space-y-4 px-0 py-4 animate-in fade-in duration-300">
        <ProductForm
          defaultValues={productToFormValues(product, { productTypeOptions, materialTypeOptions, processOptions }) as any}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          submitLabel="Cập nhật"
        />
      </div>
    </StandardDetailLayout>

  );
}
